import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  FieldFunctionOptions,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { ServerError } from "@apollo/client/link/utils";
import { CourseFeedbacksQuery } from "@src/features/contentDetail/courseDetail/feedback/FeedbackTable.generated";
import { GetNotificationsQuery } from "@src/features/app/top-nav/NotificationBell.generated";
import { HrisEmployeesQuery } from "@src/features/settings/hris/HrisEmployeeListContainer.generated";
import { HrisLocationsQuery } from "@src/features/settings/hris/HrisLocationListContainer.generated";
import { HrisTeamsQuery } from "@src/features/settings/hris/HrisRoleListContainer.generated";
import { currentRouteIsUnauthenticated } from "@src/pages/_app";
import { cleanVariablesMiddleware } from "@src/lib/graphql/middleware/cleanVariablesMiddleware";
import {
  MessageBlastsQuery,
  ReceivedMessagesQuery,
} from "@src/features/messaging/MessagingContainer.generated";
import {
  fetchLatestVercelGitCommitSha,
  getDashboardLoadedAtFromWindow,
  getLatestVercelGitCommitShaFromWindow,
  getVercelGitCommitSha,
} from "@src/features/app/utils/assetsInfo";
import { createUploadLink } from "apollo-upload-client";
import introspectionQueryResultData from "@src/fragmentTypes.json";
import { mergeActionPermissionsData, mergePaginatedData } from "./utils";
import { GetPeopleQuery } from "@src/features/users/operations.generated";
import { UserDetailAccountHistory_UserAuditItemsQuery } from "@src/features/users/user-details/UserDetailAccountHistory.generated";
import { ChatHistoryQuery } from "@src/features/assistantChat/aiChat.generated";
import { MergeBackfillRunsQuery } from "@src/features/settings/hris/HrisSyncsListContainer.generated";
import { AskOpusQuestionAnswersQuery } from "@src/features/reporting/askOpus/AskOpusQuestionAnswersList.generated";
import { HrisCsvUploadsQuery } from "@src/features/settings/hris/HrisCsvUploadsListContainer.generated";
import { OrgSftpIntegrationsQuery } from "@src/features/settings/hris/OrgSftpIntegrationsListContainer.generated";
import { ScheduledUserInvitesQuery } from "@src/features/settings/scheduled-invites/ScheduledInvitesListContainer.generated";
import { BackfilledAssignmentsQuery } from "@src/features/settings/training-imports/BackfilledAssignmentsListContainer.generated";
import {
  getImpersonateUserIdFromUrl,
  IMPERSONATION_EXEMPT_OPERATIONS,
} from "@src/features/app/hooks/useImpersonation";

const link = createUploadLink({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_URL,
  credentials: "include",
});

export const getCustomDynamicHeaders = (
  operationName?: string,
): Record<string, string> => {
  const headers: Record<string, string> = {
    "X-DASHBOARD-CURRENT-ASSETS-COMMIT-SHA": getVercelGitCommitSha() || "null",
    "X-DASHBOARD-LATEST-DEPLOYED-COMMIT-SHA":
      getLatestVercelGitCommitShaFromWindow() || "null",
    "X-DASHBOARD-LOADED-AT": getDashboardLoadedAtFromWindow() || "null",
    "X-OPUS-ROLE": "admin",
    "X-DASHBOARD-URL": window.location.href,
  };
  const impersonateUserId = getImpersonateUserIdFromUrl();
  if (
    impersonateUserId !== null &&
    (!operationName || !IMPERSONATION_EXEMPT_OPERATIONS.has(operationName))
  ) {
    headers["X-Impersonate-User-Id"] = String(impersonateUserId);
  }
  return headers;
};

const headerLink = setContext((operation, { headers }) => {
  return {
    headers: {
      ...headers,
      ...getCustomDynamicHeaders(operation.operationName),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (networkError && (networkError as ServerError).statusCode === 401) {
    if (currentRouteIsUnauthenticated()) {
      return;
    }
    window.location.href = process.env.NEXT_PUBLIC_LOGIN_URL as string;
  } else if (graphQLErrors && graphQLErrors.length > 0) {
    const oldSha = getVercelGitCommitSha();
    fetchLatestVercelGitCommitSha().then((latestSha) => {
      if (oldSha && latestSha && oldSha !== latestSha) {
        window.location.reload();
      }
    });
  }
});

const apollo = new ApolloClient({
  link: cleanVariablesMiddleware.concat(
    headerLink.concat(errorLink.concat(link as unknown as ApolloLink)),
  ),
  cache: new InMemoryCache({
    possibleTypes: introspectionQueryResultData.possibleTypes,
    dataIdFromObject: (responseObject) => {
      if (responseObject.pghId) {
        return `${responseObject.__typename}:${responseObject.pghId}`;
      } else if (responseObject.id) {
        return `${responseObject.__typename}:${responseObject.id}`;
      } else if (responseObject.uuid) {
        return `${responseObject.__typename}:${responseObject.uuid}`;
      } else {
        return defaultDataIdFromObject(responseObject);
      }
    },
    typePolicies: {
      Query: {
        fields: {
          TrainingResources: {
            merge(_, incoming: unknown[]) {
              return [...incoming];
            },
          },
          Me: {
            merge(
              existing: Record<string, unknown>,
              incoming: Record<string, unknown>,
            ) {
              return { ...existing, ...incoming };
            },
          },
          UserActionPermissions: {
            merge(
              existing: Record<string, unknown>,
              incoming: Record<string, unknown>,
            ) {
              return { ...existing, ...incoming };
            },
          },
          AdminActiveClients: {
            merge(_, incoming: unknown[]) {
              return [...incoming];
            },
          },
          AdminGroups: {
            merge(_, incoming: unknown) {
              return incoming;
            },
          },
          Locations: {
            keyArgs: ["input"],
            merge(existing, incoming, options) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          Roles: {
            keyArgs: ["input"],
            merge(existing, incoming, options) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          LocationGroups: {
            keyArgs: ["input"],
            merge(existing, incoming, options) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          RoleGroups: {
            keyArgs: ["input"],
            merge(existing, incoming, options) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          People: {
            keyArgs: ["input"],
            merge(
              existing: GetPeopleQuery["People"] | undefined,
              incoming: GetPeopleQuery["People"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          PeopleActionPermission: {
            keyArgs: ["permission", "input", "hasPermission"],
            merge(existing, incoming, options) {
              return mergeActionPermissionsData(existing, incoming, options);
            },
          },
          HrisEmployees: {
            keyArgs: ["input"],
            merge(
              existing: HrisEmployeesQuery["HrisEmployees"] | undefined,
              incoming: HrisEmployeesQuery["HrisEmployees"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          HrisCsvUploads: {
            keyArgs: ["input"],
            merge(
              existing: HrisCsvUploadsQuery["HrisCsvUploads"] | undefined,
              incoming: HrisCsvUploadsQuery["HrisCsvUploads"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          OrgSftpIntegrations: {
            keyArgs: ["input"],
            merge(
              existing:
                | OrgSftpIntegrationsQuery["OrgSftpIntegrations"]
                | undefined,
              incoming: OrgSftpIntegrationsQuery["OrgSftpIntegrations"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          MyChats: {
            keyArgs: ["input"],
            merge(existing, incoming, options) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          MessagingActivityItems: {
            keyArgs: ["filters"],
            merge(existing, incoming, options) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          ChatMessages: {
            keyArgs: ["chatId", "input"],
            merge(
              existing: { objects: unknown[]; totalCount: number } | undefined,
              incoming: { objects: unknown[]; totalCount: number },
              { readField }: FieldFunctionOptions,
            ) {
              const byId = new Map<unknown, unknown>();
              (existing?.objects || []).forEach((ref) => {
                byId.set(readField("id", ref as never), ref);
              });
              (incoming.objects || []).forEach((ref) => {
                byId.set(readField("id", ref as never), ref);
              });
              const objects = [...byId.values()].filter(
                (ref) => !readField("deletedAt", ref as never),
              );
              return { ...incoming, objects };
            },
          },
          MergeBackfillRuns: {
            keyArgs: ["input"],
            merge(
              existing: MergeBackfillRunsQuery["MergeBackfillRuns"] | undefined,
              incoming: MergeBackfillRunsQuery["MergeBackfillRuns"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          BackfilledAssignments: {
            keyArgs: ["input"],
            merge(
              existing:
                | BackfilledAssignmentsQuery["BackfilledAssignments"]
                | undefined,
              incoming: BackfilledAssignmentsQuery["BackfilledAssignments"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          ScheduledUserInvites: {
            keyArgs: ["input"],
            merge(
              existing:
                | ScheduledUserInvitesQuery["ScheduledUserInvites"]
                | undefined,
              incoming: ScheduledUserInvitesQuery["ScheduledUserInvites"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          HrisLocations: {
            keyArgs: ["input"],
            merge(
              existing: HrisLocationsQuery["HrisLocations"] | undefined,
              incoming: HrisLocationsQuery["HrisLocations"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          AutomationsV2: {
            keyArgs: ["input"],
            merge(
              existing: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          HrisTeams: {
            keyArgs: ["input"],
            merge(
              existing: HrisTeamsQuery["HrisTeams"] | undefined,
              incoming: HrisTeamsQuery["HrisTeams"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          Assignments: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          TaskScheduleCompletionPercents: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          TasksListsCompletionGrid_CompletedTaskCounts: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          SkillInstances: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          CompletedTaskSkillInstances: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          GuestFeedbacks: {
            keyArgs: ["input"],
            merge(existing, incoming, options) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          PromptInstances: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          VerificationStepInstances: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          SharpAssignments: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          AdminLibrary: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          Workflows: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          WorkflowInstances: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          Tasks: {
            keyArgs: ["input"],
            merge(existing, incoming, options) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          TraineeLibrary: {
            keyArgs: ["input"],
            merge(
              existing: any | undefined, // eslint-disable-line  @typescript-eslint/no-explicit-any
              incoming: any, // eslint-disable-line  @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          InProductNotifications: {
            keyArgs: ["input"],
            merge(
              existing: GetNotificationsQuery["Notifications"] | undefined,
              incoming: GetNotificationsQuery["Notifications"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          CourseFeedbacks: {
            keyArgs: ["input"],
            merge(
              existing: CourseFeedbacksQuery["CourseFeedbacks"] | undefined,
              incoming: CourseFeedbacksQuery["CourseFeedbacks"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          MessageBlasts: {
            keyArgs: ["input"],
            merge(
              existing: MessageBlastsQuery["MessageBlasts"] | undefined,
              incoming: MessageBlastsQuery["MessageBlasts"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          EmployeeBlastSentRecordsPaginated: {
            keyArgs: false,
            merge(
              existing:
                | ReceivedMessagesQuery["EmployeeBlastSentRecordsPaginated"]
                | undefined,
              incoming: ReceivedMessagesQuery["EmployeeBlastSentRecordsPaginated"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          ManagerLeaderboardPaginated: {
            keyArgs: ["input"],
            merge(
              existing: { objects: any[] } | undefined, // eslint-disable-line   @typescript-eslint/no-explicit-any
              incoming: { objects: any[]; totalCount: number }, // eslint-disable-line   @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          LocationLeaderboard: {
            keyArgs: ["input"],
            merge(
              existing: { objects: any[] } | undefined, // eslint-disable-line   @typescript-eslint/no-explicit-any
              incoming: { objects: any[]; totalCount: number }, // eslint-disable-line   @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          TraineeLeaderboard: {
            keyArgs: ["input"],
            merge(
              existing: { objects: any[] } | undefined, // eslint-disable-line   @typescript-eslint/no-explicit-any
              incoming: { objects: any[]; totalCount: number }, // eslint-disable-line   @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          UserAuditItems: {
            keyArgs: ["input"],
            merge(
              existing:
                | UserDetailAccountHistory_UserAuditItemsQuery["UserAuditItems"]
                | undefined,
              incoming: UserDetailAccountHistory_UserAuditItemsQuery["UserAuditItems"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          ComplianceRecords: {
            keyArgs: ["input"],
            merge(
              existing: { objects: any[] } | undefined, // eslint-disable-line   @typescript-eslint/no-explicit-any
              incoming: { objects: any[]; totalCount: number }, // eslint-disable-line   @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          CompletionScheduleWindows: {
            keyArgs: ["input"],
            merge(
              existing: { objects: any[] } | undefined, // eslint-disable-line   @typescript-eslint/no-explicit-any
              incoming: { objects: any[]; totalCount: number }, // eslint-disable-line   @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          MyAiChats: {
            keyArgs: ["input"],
            merge(
              existing: ChatHistoryQuery["MyAiChats"] | undefined,
              incoming: ChatHistoryQuery["MyAiChats"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          OpusAgentChats: {
            keyArgs: ["input"],
            merge(
              existing: { objects: any[] } | undefined, // eslint-disable-line   @typescript-eslint/no-explicit-any
              incoming: { objects: any[]; totalCount: number }, // eslint-disable-line   @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          AskOpusQuestionAnswers: {
            keyArgs: ["input"],
            merge(
              existing:
                | AskOpusQuestionAnswersQuery["AskOpusQuestionAnswers"]
                | undefined,
              incoming: AskOpusQuestionAnswersQuery["AskOpusQuestionAnswers"],
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
          LibraryItemFolderMemberships: {
            keyArgs: ["input"],
            merge(
              existing: { objects: any[] } | undefined, // eslint-disable-line   @typescript-eslint/no-explicit-any
              incoming: { objects: any[]; totalCount: number }, // eslint-disable-line   @typescript-eslint/no-explicit-any
              options: FieldFunctionOptions,
            ) {
              return mergePaginatedData(existing, incoming, options);
            },
          },
        },
      },
      TrainingResource: {
        fields: {
          children: {
            merge(_, incoming: unknown[]) {
              return [...incoming];
            },
          },
        },
      },
      ContentTagMembership: {
        keyFields: ["tagId", "libraryItemId"],
      },
      ActiveClient: {
        keyFields: ["clientId"],
      },
      ImpactReportingStoreOpening: {
        keyFields: ["locationId"],
      },
      Group: {
        fields: {
          employees: {
            merge(_, incoming: unknown[]) {
              return [...incoming];
            },
          },
        },
      },
      Employee: {
        fields: {
          roles: {
            merge(_, incoming: unknown[]) {
              return [...incoming];
            },
          },
          locations: {
            merge(_, incoming: unknown[]) {
              return [...incoming];
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
});

export default apollo;
